import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { isNil } from "ramda";
import { Button, Dialog, Slide, Typography, CircularProgress } from "@mui/material";

import AWSRepository from "repositories/AWSRepository";
import { dataURItoBlob } from "utils/imageUtils";
import {
  useScreen,
  useTemplateGroup,
  useCasting,
  usePeople,
  useSaveMakeAndGo,
  useShares,
  useRouter,
  useAssetUpload,
} from "hooks";
import SpinningCloseButton from "components/SpinningCloseButton";
import HTMLPlayer from "components/HTMLPlayer";
import PersonPresenter from "presenters/PersonPresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import DisplayPerson from "components/DisplayPerson";
import { DATE_TYPE } from "enums";
import { getOccasionKeyByValue } from "utils/relativeUtils";
import { trackCalendarDiscovery } from "tracking/google/analytics";
import { trackCordialMakeCreated } from "utils/cordialUtils";
import { shouldAddNextYear } from "utils/dateTimeUtils";
import { capitalize } from "utils/stringUtils";
import selectedCastNumber from "utils/selectedCastNumber";
import { jjLogger } from "utils/logUtils";
import { appRoutes } from "routes";

import useStyles from "./useStyles";

const CAST_COUNT = 1;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CalendarDetailModal = ({ person, dateType, dateToCelebrate, onClose, items }) => {
  const classes = useStyles();
  const { isDesktop } = useScreen();
  const { setDateToShare } = useShares();
  const { push } = useRouter();
  const { uploadThumbAssets } = useAssetUpload();
  const { loadTemplateGroup, templateGroup } = useTemplateGroup();
  const { card, init, cleanup, CONSTANTS, castFace, getData } = useCasting();
  const { updatePerson, peopleToUpdate, resetPeopleToUpdate, loadPeopleToCast } = usePeople();

  const [templateGroupsInfo, setTemplateGroupsInfo] = useState([]);
  const [currentTemplateGroupInfo, setCurrentTemplateGroupInfo] = useState({});
  const [celebrateDate, setCelebrateDate] = useState("");
  const [isChangeVideo, setisChangeVideo] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);

  const face = PersonPresenter.defaultHead(person);
  const faces = [face];
  const rootClasses = clsx(classes[getOccasionKeyByValue(dateType)]);
  const src = TemplateGroupPresenter.previewVideo(currentTemplateGroupInfo);

  const saveAndMakeSuccess = (currentUser, make) => {
    setDateToShare(celebrateDate);
    trackCordialMakeCreated(make, templateGroup);
  };
  const { saveMakeAndGo } = useSaveMakeAndGo(saveAndMakeSuccess, templateGroup);

  const shuffleTemplate = () => {
    const allTemplates = isNil(templateGroupsInfo) ? [] : [...templateGroupsInfo];

    if (currentTemplateGroupInfo) {
      const currentTemplateIndex = allTemplates.findIndex((template) => template.id === currentTemplateGroupInfo.id);
      allTemplates.splice(currentTemplateIndex, 1);
    }

    const randomTemplate = allTemplates[Math.floor(Math.random() * allTemplates.length)];
    setCurrentTemplateGroupInfo(randomTemplate);
  };

  const handleShuffleTemplate = () => {
    shuffleTemplate();
    trackCalendarDiscovery("Shuffle");
  };

  const handleStoreMake = async () => {
    const { roles, makeThumbUrl, shareThumbUrl, templateId } = getData();
    const actions = uploadThumbAssets([
      AWSRepository.presignedUploadToS3({ method: "make_thumb" }, dataURItoBlob(makeThumbUrl)),
      AWSRepository.presignedUploadToS3({ method: "share_thumb" }, dataURItoBlob(shareThumbUrl)),
    ]);

    return actions.then((results) => {
      const [makeThumb, shareThumb] = results;
      peopleToUpdate.map(updatePerson);
      resetPeopleToUpdate();

      return saveMakeAndGo({ roles, templateId, makeThumb, shareThumb });
    });
  };

  const handleCloseModal = () => {
    if (disabledButton) return;
    onClose();
  };

  const handleSchedule = () => {
    loadTemplateGroup(currentTemplateGroupInfo.id);
    setDisabledButton(true);
    trackCalendarDiscovery("Schedule now");
  };

  const handleEdit = () => {
    loadPeopleToCast([person]);
    selectedCastNumber.set(CAST_COUNT);
    trackCalendarDiscovery("Edit");
    push(appRoutes.newCastPath(currentTemplateGroupInfo.id));
  };

  const Title = () => {
    const dateFormat = "MMMM D, YYYY";
    const title = `${capitalize(dateType)} Ecard`;

    const celebrationDate = shouldAddNextYear(dateToCelebrate);

    const subtitle = `Schedule it now for ${celebrationDate.format(dateFormat)}`;

    setCelebrateDate(celebrationDate.format("YYYY-MM-DD"));
    return (
      <div className={classes.textContent}>
        <Typography variant="h3" className={classes.text}>
          Send a
        </Typography>
        <Typography variant="h3" className={classes.title}>
          {title}
        </Typography>
        <Typography variant="h3">{subtitle}</Typography>
      </div>
    );
  };

  useEffect(() => {
    if (items.length > 0) setTemplateGroupsInfo(items);
  }, [items]);

  useEffect(shuffleTemplate, [templateGroupsInfo]);

  useEffect(() => {
    if (src != null) {
      setisChangeVideo(true);
      setTimeout(() => {
        setisChangeVideo(false);
      }, 100);
    }
  }, [currentTemplateGroupInfo]);

  useEffect(() => {
    if (isNil(templateGroup) || card) return undefined;
    init(faces, templateGroup);
    return () => cleanup();
  }, [templateGroup]);

  useEffect(() => {
    if (card) {
      jjLogger.log(`CalendarDetailModal: Calling castFace()`);
      castFace(face).then(() => {
        handleStoreMake();
      });
    }
  }, [card]);

  return (
    <Dialog
      classes={{ paper: classes.paper, container: rootClasses }}
      maxWidth={isDesktop ? "lg" : ""}
      open
      onClose={handleCloseModal}
      TransitionComponent={Transition}
    >
      <div hidden id={CONSTANTS.canvasId} />
      <SpinningCloseButton
        data-testid="calendarDetailModal-closeButton"
        onClick={handleCloseModal}
        className={classes.closeButton}
      />
      <div className={classes.container}>
        <section className={classes.info}>
          <DisplayPerson
            showLabel
            person={person}
            noOpacity
            showBadge={false}
            hasPartyHat={dateType === DATE_TYPE.birthday}
            dateType={dateType}
          />
          <Title />
        </section>

        <section className={classes.actions}>
          <section className={classes.playerContainer}>
            <HTMLPlayer
              className={classes.video}
              src={src}
              poster={TemplateGroupPresenter.posterUrl(currentTemplateGroupInfo)}
              autoplay={false}
              joinable={false}
              controls
              isChangeVideo={isChangeVideo}
              aspectRatio="16/9"
            />
            <div className={classes.playerActions}>
              <Button
                onClick={handleEdit}
                variant="contained"
                size="small"
                className={classes.buttonEdit}
                disabled={disabledButton}
              >
                Edit
              </Button>
              <Typography variant="small">
                {!isNil(currentTemplateGroupInfo) &&
                  `${TemplateGroupPresenter.firstCastNum(currentTemplateGroupInfo)} Person`}
              </Typography>
            </div>
          </section>
          <div className={classes.buttons}>
            <Button
              variant="outlined"
              color="primary"
              size={isDesktop ? "large" : "medium"}
              className={clsx(classes.button, classes.buttonOrder)}
              onClick={handleShuffleTemplate}
              disabled={disabledButton}
            >
              Shuffle
            </Button>
            <Button
              variant="contained"
              color="primary"
              size={isDesktop ? "large" : "medium"}
              className={clsx(classes.button, classes.schedule)}
              onClick={handleSchedule}
              disabled={disabledButton}
            >
              Schedule now!
              {disabledButton && <CircularProgress size={24} className={classes.buttonProgress} />}
            </Button>
          </div>
        </section>
      </div>
    </Dialog>
  );
};

CalendarDetailModal.propTypes = {
  person: PersonPresenter.shape().isRequired,
  dateType: PropTypes.string.isRequired,
  dateToCelebrate: PropTypes.string,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.shape()),
};

export default CalendarDetailModal;
